import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnimationStatusQuery, type AnimationStatusResponse } from '@/services/avatarApi';
import { Loader } from '@/components/common/Loader';
import { cn } from '@/lib/cn';

interface Props {
  avatarId: string;
  onComplete: () => void;
  onFailed: (reason: string) => void;
}

export function AnimationOverlay({ avatarId, onComplete, onFailed }: Props) {
  const { t } = useTranslation('avatars');
  const { data: status } = useAnimationStatusQuery(avatarId, { pollingInterval: 4000 });

  useEffect(() => {
    if (!status) return;
    if (status.phase === 'ready') onComplete();
    else if (status.phase === 'failed') onFailed(status.failureReason ?? 'animation_failed');
  }, [status?.phase, status?.failureReason, onComplete, onFailed]);

  const phaseLabels: Record<AnimationStatusResponse['phase'], string> = {
    queued: t('animationOverlay.phase.queued'),
    rigging: t('animationOverlay.phase.rigging'),
    retargeting: t('animationOverlay.phase.retargeting'),
    merging: t('animationOverlay.phase.merging'),
    uploading: t('animationOverlay.phase.uploading'),
    ready: t('animationOverlay.phase.ready'),
    failed: t('animationOverlay.phase.failed'),
    unknown: t('animationOverlay.phase.unknown'),
  };
  const phaseLabel = status ? phaseLabels[status.phase] : t('animationOverlay.starting');
  const pct = Math.min(100, Math.max(0, status?.overallPct ?? 0));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 px-6">
      <div className="flex w-full max-w-sm flex-col items-center rounded-2xl bg-bg-secondary p-6">
        <Loader className="!py-0" />
        <h2 className="mt-4 text-lg font-bold text-fg">{t('animationOverlay.title')}</h2>
        <p className="mt-1 text-sm text-fg-muted">{phaseLabel}</p>
        <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-bg-tertiary">
          <div
            className="h-full rounded-full bg-brand transition-[width] duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="mt-1 text-xs text-fg-muted">{pct}%</p>
        {status?.animations && status.animations.length > 0 && (
          // Phase 3 — dynamic grid that fits up to 6 columns; auto-wraps for 7-12+ clips
          <div
            className="mt-4 grid gap-2"
            style={{
              gridTemplateColumns: `repeat(${Math.min(status.animations.length, 6)}, minmax(0, 1fr))`,
            }}
          >
            {status.animations.map((a) => (
              <span
                key={a.preset}
                className={cn(
                  'h-2 w-2 rounded-full',
                  a.status === 'ready' && 'bg-brand',
                  a.status === 'pending' && 'bg-brand/40 animate-pulse',
                  a.status === 'queued' && 'bg-bg-tertiary',
                  a.status === 'failed' && 'bg-danger',
                )}
                title={`${a.preset}: ${a.status}`}
              />
            ))}
          </div>
        )}
        {status?.etaSeconds && status.phase !== 'ready' && (
          <p className="mt-2 text-[11px] text-fg-muted">
            {t('animationOverlay.eta', { minutes: Math.ceil(status.etaSeconds / 60) })}
          </p>
        )}
      </div>
    </div>
  );
}
